import { z } from 'zod';
import { GRAPH_NODE_DEFS } from './node-defs';
export { DesktopUpdateStateSchema } from './desktop-update';
export type { DesktopUpdateState } from './desktop-update';

const graphId = z.string().min(1).max(160);
const coordinate = z.number().finite();
const hexColor = z.string().regex(/^#[0-9a-f]{6}$/i);

/** An explicit, read-only credential check; readiness does not guarantee credit or model access. */
export const StudioProviderConnectionSchema = z.object({
  status: z.enum(['missing', 'ready', 'invalid', 'unavailable']),
  message: z.string(),
});
export type StudioProviderConnection = z.infer<typeof StudioProviderConnectionSchema>;

/** Settings remain extensible, but documents must survive a JSON round trip. */
export const StudioParametersSchema = z.record(z.string(), z.unknown()).superRefine((value, ctx) => {
  const seen = new Set<object>();
  const visit = (item: unknown, depth: number): boolean => {
    if (item === null || typeof item === 'string' || typeof item === 'boolean') return true;
    if (typeof item === 'number') return Number.isFinite(item);
    if (typeof item !== 'object' || depth > 64 || seen.has(item)) return false;
    if (!Array.isArray(item) && Object.getPrototypeOf(item) !== Object.prototype && Object.getPrototypeOf(item) !== null) return false;
    seen.add(item);
    const valid = Object.values(item).every((child) => visit(child, depth + 1));
    seen.delete(item);
    return valid;
  };
  if (!visit(value, 0)) ctx.addIssue({ code: 'custom', message: 'Graph settings must contain finite JSON values.' });
});
const parameters = StudioParametersSchema;

export const GraphPortSchema = z.object({
  id: graphId,
  type: z.enum(['image', 'text', 'any', 'video', 'audio', 'mesh']),
}).loose();

export const GraphExposedParameterSchema = z.object({
  key: graphId,
  nodeId: graphId,
  field: graphId,
  type: z.enum(['text', 'number', 'model']),
  value: z.unknown(),
  label: z.string().max(500),
}).loose();

export const GraphNodeDefSchema = z.object({
  label: z.string().min(1).max(500),
  category: z.string().min(1).max(160),
  icon: z.string().max(160),
  inputs: z.array(GraphPortSchema).max(100),
  outputs: z.array(GraphPortSchema).max(100),
  params: parameters,
  custom: z.string().max(160).optional(),
  exposed: z.array(GraphExposedParameterSchema).max(200).optional(),
  /** Kept so saved graphs still run, but no longer offered in the node palette. */
  legacy: z.boolean().optional(),
}).loose();

/** Unknown node types and additional fields are retained, never converted to notes. */
export const GraphDocNodeSchema = z.object({
  id: graphId,
  type: z.string().min(1).max(200),
  x: coordinate,
  y: coordinate,
  params: parameters,
}).loose();

export const GraphDocEdgeSchema = z.object({
  id: graphId,
  source: graphId,
  sourceHandle: z.string().max(160).optional(),
  target: graphId,
  targetHandle: z.string().max(160).optional(),
}).loose();

export const GraphDocSchema = z.object({
  nodes: z.array(GraphDocNodeSchema).max(10000),
  edges: z.array(GraphDocEdgeSchema).max(20000),
}).loose().superRefine((doc, ctx) => {
  if (!parameters.safeParse(doc).success) ctx.addIssue({ code: 'custom', message: 'Graph documents must contain finite JSON values, including extension fields.' });
  const nodeIds = new Set<string>();
  for (const [index, node] of doc.nodes.entries()) {
    if (nodeIds.has(node.id)) ctx.addIssue({ code: 'custom', path: ['nodes', index, 'id'], message: 'Node IDs must be unique.' });
    nodeIds.add(node.id);
  }
  const edgeIds = new Set<string>();
  for (const [index, edge] of doc.edges.entries()) {
    if (edgeIds.has(edge.id)) ctx.addIssue({ code: 'custom', path: ['edges', index, 'id'], message: 'Edge IDs must be unique.' });
    edgeIds.add(edge.id);
    for (const end of ['source', 'target'] as const) {
      if (!nodeIds.has(edge[end])) ctx.addIssue({ code: 'custom', path: ['edges', index, end], message: 'An edge must refer to a node in this graph.' });
    }
  }
});

/**
 * Saving a draft and authorizing execution are separate operations.
 *
 * `definitions` defaults to the built-in operation catalogue; a host passes its own map when a
 * session also has imported ComfyUI workflows, or when a build ships fewer operations than it
 * knows how to validate.
 */
export function graphExecutionIssues(doc: GraphDoc, definitions: Record<string, GraphNodeDef> = GRAPH_NODE_DEFS): string[] {
  const issues: string[] = [];
  const parsed = GraphDocSchema.safeParse(doc);
  if (!parsed.success) return parsed.error.issues.map((issue) => issue.message);
  const definition = (type: string) => Object.hasOwn(definitions, type) ? definitions[type] : undefined;
  const nodes = new Map(doc.nodes.map((node) => [node.id, node]));
  const indegree = new Map(doc.nodes.map((node) => [node.id, 0]));
  const outgoing = new Map<string, string[]>();
  for (const node of doc.nodes) {
    if (!definition(node.type)) issues.push(`Tool unavailable: ${node.type} (${node.id}).`);
  }
  for (const edge of doc.edges) {
    const source = nodes.get(edge.source);
    const target = nodes.get(edge.target);
    if (!source || !target) { issues.push(`Connection ${edge.id} refers to a missing node.`); continue; }
    indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
    const targets = outgoing.get(edge.source) ?? [];
    targets.push(edge.target);
    outgoing.set(edge.source, targets);
    const from = definition(source.type);
    const to = definition(target.type);
    if (!from || !to) continue;
    const output = from.outputs.find((port) => port.id === (edge.sourceHandle ?? 'out'));
    const input = to.inputs.find((port) => port.id === (edge.targetHandle ?? 'in'));
    if (!output || !input) issues.push(`Connection ${edge.id} uses an unavailable port.`);
    else if (output.type !== 'any' && input.type !== 'any' && output.type !== input.type) issues.push(`Connection ${edge.id} has incompatible port types.`);
  }
  const queue = doc.nodes.filter((node) => indegree.get(node.id) === 0).map((node) => node.id);
  for (let index = 0; index < queue.length; index++) {
    for (const target of outgoing.get(queue[index]!) ?? []) {
      const remaining = (indegree.get(target) ?? 0) - 1;
      indegree.set(target, remaining);
      if (remaining === 0) queue.push(target);
    }
  }
  if (queue.length !== doc.nodes.length) issues.push('The graph contains a cycle.');
  return issues;
}

export type GraphPort = z.infer<typeof GraphPortSchema>;
export type GraphNodeDef = z.infer<typeof GraphNodeDefSchema>;
export type GraphExposedParameter = z.infer<typeof GraphExposedParameterSchema>;
export type GraphDocNode = z.infer<typeof GraphDocNodeSchema>;
export type GraphDocEdge = z.infer<typeof GraphDocEdgeSchema>;
export type GraphDoc = z.infer<typeof GraphDocSchema>;

export const StudioAssetSchema = z.object({
  id: graphId,
  name: z.string().min(1).max(500),
  mediaType: z.enum(['image', 'video', 'audio', 'mesh', 'file']),
  mimeType: z.string().min(1).max(160),
  size: z.number().int().nonnegative(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
}).loose();

export const StudioProvenanceSchema = z.object({
  graphId,
  nodeId: graphId,
  jobId: graphId.optional(),
  inputVersions: z.array(z.object({ frameId: graphId, versionId: graphId }).strict()).optional(),
  model: z.string().max(500).optional(),
  settings: parameters.optional(),
}).strict();

export const StudioFrameVersionSchema = z.object({
  id: graphId,
  label: z.string().max(500),
  assetId: graphId.optional(),
  jobId: graphId.nullable().optional(),
  parentVersionId: graphId.nullable().optional(),
  params: parameters,
  provenance: StudioProvenanceSchema.optional(),
}).loose();

export const StudioFrameSchema = z.object({
  id: graphId,
  title: z.string().max(500),
  kind: z.enum(['media', 'board', 'note', 'section', 'web', 'design', 'tool', 'composition']),
  x: coordinate,
  y: coordinate,
  w: z.number().finite().positive(),
  h: z.number().finite().positive(),
  activeVersion: z.number().int().nonnegative(),
  params: parameters,
  versions: z.array(StudioFrameVersionSchema).max(10000),
}).loose().superRefine((frame, ctx) => {
  if (frame.activeVersion >= Math.max(frame.versions.length, 1)) {
    ctx.addIssue({ code: 'custom', path: ['activeVersion'], message: 'The active version must exist on this frame.' });
  }
});

export const StudioGraphSchema = z.object({
  id: graphId,
  name: z.string().min(1).max(500),
  doc: GraphDocSchema,
  source: z.enum(['desktop', 'web']).optional(),
}).loose();

/**
 * One portable session for Art and Node views. Asset bytes travel separately,
 * keyed by asset ID and verified by SHA-256; machine paths are not asset IDs.
 * Account ownership, credentials, execution permissions, and sync revisions
 * belong to the host and must never be trusted from an imported document.
 */
export const StudioProjectSchema = z.object({
  schemaVersion: z.literal(1),
  id: graphId,
  title: z.string().min(1).max(500),
  artboards: z.array(StudioFrameSchema).max(10000),
  graphs: z.array(StudioGraphSchema).max(1000),
  assets: z.array(StudioAssetSchema).max(20000),
  primaryGraphId: graphId.optional(),
}).loose().superRefine((project, ctx) => {
  if (!parameters.safeParse(project).success) ctx.addIssue({ code: 'custom', message: 'Projects must contain finite JSON values, including extension fields.' });
  for (const key of ['artboards', 'graphs', 'assets'] as const) {
    const ids = new Set<string>();
    project[key].forEach((item, index) => {
      if (ids.has(item.id)) ctx.addIssue({ code: 'custom', path: [key, index, 'id'], message: `${key} IDs must be unique.` });
      ids.add(item.id);
    });
  }
  const graphs = new Map(project.graphs.map((graph) => [graph.id, graph]));
  const assets = new Set(project.assets.map((asset) => asset.id));
  const versions = new Set<string>();
  const frameVersions = new Map(project.artboards.map((frame) => [frame.id, new Set(frame.versions.map((version) => version.id))]));
  if (project.primaryGraphId && !graphs.has(project.primaryGraphId)) {
    ctx.addIssue({ code: 'custom', path: ['primaryGraphId'], message: 'The primary graph must belong to this project.' });
  }
  project.artboards.forEach((frame, frameIndex) => frame.versions.forEach((version, versionIndex) => {
    const path = ['artboards', frameIndex, 'versions', versionIndex];
    if (versions.has(version.id)) ctx.addIssue({ code: 'custom', path: [...path, 'id'], message: 'Version IDs must be unique across the project.' });
    versions.add(version.id);
    if (version.assetId && !assets.has(version.assetId)) ctx.addIssue({ code: 'custom', path: [...path, 'assetId'], message: 'The version asset must be included in the project.' });
    if (version.provenance) {
      const graph = graphs.get(version.provenance.graphId);
      if (!graph?.doc.nodes.some((node) => node.id === version.provenance?.nodeId)) ctx.addIssue({ code: 'custom', path: [...path, 'provenance'], message: 'The producing node must belong to a project graph.' });
    }
  }));
  const parents = new Map(project.artboards.flatMap((frame) => frame.versions.map((version) => [version.id, version.parentVersionId] as const)));
  const checked = new Set<string>();
  for (const frame of project.artboards) {
    for (const version of frame.versions) {
      if (version.parentVersionId && !versions.has(version.parentVersionId)) {
        ctx.addIssue({ code: 'custom', message: `Version ${version.id} refers to a missing parent version.` });
      }
      for (const input of version.provenance?.inputVersions ?? []) {
        if (!frameVersions.get(input.frameId)?.has(input.versionId)) {
          ctx.addIssue({ code: 'custom', message: `Version ${version.id} refers to a missing input version.` });
        }
      }
      const chain = new Set<string>();
      let cursor: string | null | undefined = version.id;
      while (cursor && parents.has(cursor) && !checked.has(cursor)) {
        if (chain.has(cursor)) { ctx.addIssue({ code: 'custom', message: 'Frame version history contains a cycle.' }); break; }
        chain.add(cursor);
        cursor = parents.get(cursor);
      }
      for (const id of chain) checked.add(id);
    }
  }
});

export type StudioAsset = z.infer<typeof StudioAssetSchema>;
export type StudioFrameVersion = z.infer<typeof StudioFrameVersionSchema>;
export type StudioFrame = z.infer<typeof StudioFrameSchema>;
export type StudioGraph = z.infer<typeof StudioGraphSchema>;
export type StudioProject = z.infer<typeof StudioProjectSchema>;

/** Web renderer state is retained verbatim, including unknown future nodes. */
export const StudioWebNodeSchema = z.object({
  id: graphId,
  type: z.string().min(1).max(200),
  position: z.object({ x: coordinate, y: coordinate }).loose(),
  data: parameters,
}).loose();
export const StudioWebEdgeSchema = z.object({
  id: graphId, source: graphId, target: graphId,
  sourceHandle: z.string().max(160).nullable().optional(),
  targetHandle: z.string().max(160).nullable().optional(),
}).loose();
export const StudioWebPageSchema = z.object({
  nodes: z.array(StudioWebNodeSchema).max(10000),
  edges: z.array(StudioWebEdgeSchema).max(20000),
}).loose();

export const StudioDesktopVersionSchema = z.object({
  id: graphId, label: z.string().max(500), params: parameters,
  imagePath: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  jobId: graphId.nullable().optional(),
  parentVersionId: graphId.nullable().optional(),
}).loose();
export const StudioDesktopFrameSchema = z.object({
  id: graphId, title: z.string().max(500), kind: StudioFrameSchema.shape.kind,
  x: coordinate, y: coordinate, w: z.number().finite().positive(), h: z.number().finite().positive(),
  activeVersion: z.number().int().nonnegative(), params: parameters,
  versions: z.array(StudioDesktopVersionSchema),
}).loose();
export const StudioDesktopSessionSchema = z.object({
  id: graphId, title: z.string().min(1).max(500),
  artboards: z.array(StudioDesktopFrameSchema).max(10000),
  graphs: z.array(z.object({ id: graphId, name: z.string().min(1).max(500), doc: GraphDocSchema }).loose()).max(1000),
}).loose();

export const StudioFrameInputSchema = z.object({ frameId: graphId, versionId: graphId }).strict();
export const StudioFrameOutputSchema = z.object({
  frameId: graphId,
  expectedVersionId: graphId.nullable(),
  version: StudioFrameVersionSchema,
}).strict();
export const StudioExecutionLocationSchema = z.enum(['desktop', 'cloud']);
export const StudioToolRequestSchema = z.object({
  toolId: graphId, location: StudioExecutionLocationSchema,
  inputs: z.array(StudioFrameInputSchema), params: parameters,
}).strict();
export const StudioToolResultSchema = z.object({
  jobId: graphId, assets: z.array(StudioAssetSchema),
}).strict();
export type StudioWebPage = z.infer<typeof StudioWebPageSchema>;
export type StudioDesktopSession = z.infer<typeof StudioDesktopSessionSchema>;
export type StudioFrameInput = z.infer<typeof StudioFrameInputSchema>;
export type StudioFrameOutput = z.infer<typeof StudioFrameOutputSchema>;
export type StudioToolRequest = z.infer<typeof StudioToolRequestSchema>;
export type StudioToolResult = z.infer<typeof StudioToolResultSchema>;
export type StudioExecutionLocation = z.infer<typeof StudioExecutionLocationSchema>;

/** Revision tokens are assigned by the host, never trusted from imported documents. */
export const StudioSnapshotSchema = z.object({
  revision: z.number().int().nonnegative(), project: StudioProjectSchema,
}).strict();
export const StudioSaveSchema = z.object({
  expectedRevision: z.number().int().nonnegative(), project: StudioProjectSchema,
}).strict();
export const StudioAssetUploadSchema = z.object({
  asset: StudioAssetSchema, base64: z.string().max(32 * 1024 * 1024),
}).strict();

// ── Frame sidecars ────────────────────────────────────────────────────
// What a frame is beyond its picture: where it stands in a scene, the files that describe it, and
// whether a batch may touch it. Flat keys on `params`, defined once, so the desktop, the web app, the
// engine and the agent read one shape. Image references are output paths on the engine and host URLs
// once served; `foot` and `ground.box` are relative to the frame's own box, so moving the frame moves
// them with it.
const frameSize = z.number().finite().positive();
/** `[x, y, w, h]` in canvas units. */
export const FrameBoxSchema = z.tuple([coordinate, coordinate, frameSize, frameSize]);
/** `[x, y]` in canvas units. */
export const FramePointSchema = z.tuple([coordinate, coordinate]);
/** One level of a big picture: the smallest level at or above the on-screen width is drawn. */
export const FrameImageLevelSchema = z.object({
  width: z.number().int().positive(), image: z.string().min(1).max(2000),
}).strict();
/**
 * How one class of a class map (a zone) treats the frames standing on the picture, and how it is drawn in
 * the zones editor. Every key is optional: what is left out follows the zone's name (`zoneRule`).
 */
export const FieldClassRuleSchema = z.object({
  /** The zone's colour over the picture, `#rrggbb`. */
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  /** Who may put a foot on it: everyone, no one, or only frames carrying `flag` (a boat on water). */
  feet: z.enum(['all', 'none', 'flagged']).optional(),
  flag: z.string().regex(/^[\w-]{1,40}$/).optional(),
  /** Frames keep their paint off it (a tree, a building): a frame covering it breaks the rule. */
  keepClear: z.boolean().optional(),
  note: z.string().max(300).optional(),
}).strict();
/** A field the canvas samples over the frame's box (a depth map, a class map), stored at any resolution. */
export const FrameFieldSchema = z.object({
  image: z.string().min(1).max(2000), width: z.number().int().positive(), height: z.number().int().positive(),
  /** Class maps: what each pixel value means. Code 0 is the base zone the eraser gives back to. */
  legend: z.record(z.string(), z.string().max(200)).optional(),
  /** Class maps: each zone's rule, by code. */
  rules: z.record(z.string(), FieldClassRuleSchema).optional(),
}).strict();
/** A frame's contact layer (shadow, ripples, trampled ground), drawn under every frame. */
export const FrameGroundSchema = z.object({
  image: z.string().min(1).max(2000), small: z.string().max(2000).optional(),
  box: FrameBoxSchema, strength: z.number().min(0).max(1).optional(),
}).strict();
/** A frame's latest deep-zoom export (a Compositor's scene or a section as a folder for the web), in outputs. */
export const FrameDeepZoomSchema = z.object({
  id: z.string().regex(/^[a-f0-9]{8}$/),
  /** The index page, an outputs path (`deepzoom/<id>/index.html`). */
  index: z.string().max(200),
  at: z.string().max(80),
  width: z.number().int().positive(), height: z.number().int().positive(),
  items: z.number().int().min(0),
  stills: z.boolean().optional(),
}).strict();
/** Why a frame is locked. A locked frame is never moved, edited or re-cut by a batch or the agent. */
export const FrameLockSchema = z.object({
  by: z.enum(['hand', 'import', 'agent', 'user']).optional(), at: z.string().max(80).optional(), note: z.string().max(500).optional(),
}).strict();
export const FrameSidecarParamsSchema = z.object({
  locked: z.boolean().optional(),
  lock: FrameLockSchema.optional(),
  /** Drawn beneath every other frame, never selected or dragged (a plate, a backdrop). */
  background: z.boolean().optional(),
  /** Draw order offset; the order is `layer + depth at the foot`. */
  layer: z.number().finite().optional(),
  /** Where the frame stands, relative to its box. */
  foot: FramePointSchema.optional(),
  /** 0 far to 1 near, the last sampled depth at the foot (the canvas re-samples when it has the field). */
  depth: z.number().min(0).max(1).optional(),
  /** A small copy (about 256 px) drawn while the frame is small on screen; also its hit map. */
  small: z.string().max(2000).optional(),
  /** The last mask painted on the frame (white on black, an outputs file), kept for the next inpaint. */
  mask: z.string().max(2000).optional(),
  pyramid: z.array(FrameImageLevelSchema).max(16).optional(),
  ground: FrameGroundSchema.optional(),
  fields: z.object({ depth: FrameFieldSchema.optional(), classes: FrameFieldSchema.optional() }).strict().optional(),
  /** Audit flags, free strings (`bg`, `cut`, `tone`, …). */
  flags: z.array(z.string().max(80)).max(64).optional(),
  deepZoom: FrameDeepZoomSchema.optional(),
  /** Project data that is not Genie's business (a number, a topic, a panel name). */
  meta: z.record(z.string(), z.union([z.string().max(4000), z.number(), z.boolean(), z.null()])).optional(),
  /** Where an imported frame came from. */
  origin: z.object({ source: z.string().max(2000), key: z.string().max(160).optional(), importedAt: z.string().max(80).optional() }).strict().optional(),
});
export type FrameBox = z.infer<typeof FrameBoxSchema>;
export type FramePoint = z.infer<typeof FramePointSchema>;
export type FrameImageLevel = z.infer<typeof FrameImageLevelSchema>;
export type FrameField = z.infer<typeof FrameFieldSchema>;
export type FieldClassRule = z.infer<typeof FieldClassRuleSchema>;
export type FrameGround = z.infer<typeof FrameGroundSchema>;
export type FrameLock = z.infer<typeof FrameLockSchema>;
export type FrameDeepZoom = z.infer<typeof FrameDeepZoomSchema>;
export type FrameSidecarParams = z.infer<typeof FrameSidecarParamsSchema>;

/** A zone of a class map with its rule settled: what the field says, else what its name says. */
export interface ZoneRule {
  code: number;
  name: string;
  color: string;
  feet: 'all' | 'none' | 'flagged';
  flag?: string;
  keepClear: boolean;
  note?: string;
}
/** Names that read as ground a figure may stand on. */
export const ZONE_STANDABLE = /ground|water|floor|road|sand|grass|path|earth|field|snow|street|beach|soil|dirt|lawn|meadow|pond/i;
export const ZONE_SKY = /\bsky\b|cloud|heaven/i;
export const ZONE_TREES = /tree|bush|hedge|shrub|plant|foliage|wood|forest/i;
const ZONE_COLORS: [RegExp, string][] = [
  [ZONE_SKY, '#966eff'], [/water|lake|river|sea/i, '#288cff'], [ZONE_TREES, '#ff3c3c'],
  [/building|tower|wall|house|rock|solid|stone/i, '#ff961e'], [/pond/i, '#00e6d2'], [ZONE_STANDABLE, '#ebc85a'],
];
const ZONE_SPARE = ['#e93d82', '#8e4ec6', '#12a594', '#f7d544', '#46a758', '#4c9ffb'];
/**
 * The rule of zone `code` in a class map field. Left out of `field.rules`: sky takes no feet and may be
 * covered, ground-like names take every foot, trees and anything else solid take none and are kept clear.
 */
export function zoneRule(field: Pick<FrameField, 'legend' | 'rules'> | null | undefined, code: number): ZoneRule {
  const name = field?.legend?.[String(code)] ?? (code === 0 ? 'ground' : `zone ${code}`);
  const set: FieldClassRule = field?.rules?.[String(code)] ?? {};
  const sky = ZONE_SKY.test(name), ground = !sky && ZONE_STANDABLE.test(name);
  const color = set.color ?? ZONE_COLORS.find(([pattern]) => pattern.test(name))?.[1] ?? ZONE_SPARE[code % ZONE_SPARE.length];
  const feet = set.feet ?? (ground ? 'all' : 'none');
  return {
    code, name, color, feet,
    ...(feet === 'flagged' && set.flag ? { flag: set.flag } : {}),
    keepClear: set.keepClear ?? (!sky && !ground),
    ...(set.note ? { note: set.note } : {}),
  };
}
/** Every zone the field names, code 0 (the base) first. */
export function zoneRules(field: Pick<FrameField, 'legend' | 'rules'> | null | undefined): ZoneRule[] {
  const codes = new Set<number>([0]);
  for (const key of [...Object.keys(field?.legend ?? {}), ...Object.keys(field?.rules ?? {})]) {
    const code = Number(key);
    if (Number.isInteger(code) && code >= 0 && code < 256) codes.add(code);
  }
  return [...codes].sort((a, b) => a - b).map((code) => zoneRule(field, code));
}
/** Whether a frame carrying `flags` may put its foot on the zone. */
export function zoneTakesFeet(rule: Pick<ZoneRule, 'feet' | 'flag'>, flags: readonly string[] = []): boolean {
  return rule.feet === 'all' || (rule.feet === 'flagged' && Boolean(rule.flag) && flags.includes(rule.flag!));
}

/** The draw order of a frame: its layer plus the depth at its foot (0 far, 1 near). */
export function frameOrder(params: Pick<FrameSidecarParams, 'layer' | 'depth'>, depthAtFoot?: number | null): number {
  const depth = typeof depthAtFoot === 'number' && Number.isFinite(depthAtFoot) ? depthAtFoot : (params.depth ?? 0);
  return (params.layer ?? 0) + depth;
}

// ── Canvas import ─────────────────────────────────────────────────────
// A folder of frames with positions, versions and sidecars becomes a canvas. Image paths are relative
// to the folder the manifest is imported from (the engine resolves them, links or copies the files).
export const CanvasImportVersionSchema = z.object({
  label: z.string().max(200),
  image: z.string().min(1).max(2000),
  at: z.string().max(80).optional(),
  /** This version's own sidecars (its ground, its box) and any project data. */
  params: parameters.optional(),
}).strict();
export const CanvasImportFrameSchema = z.object({
  /** Stable within the folder; becomes the frame's file folder in the outputs. */
  key: z.string().regex(/^[\w-]{1,120}$/),
  title: z.string().max(500),
  kind: z.enum(['media', 'note', 'section']).optional(),
  x: coordinate, y: coordinate, w: frameSize, h: frameSize,
  /** Pixel size of the picture when it differs from the box (a plate that exists only as tiles). */
  width: z.number().int().positive().optional(), height: z.number().int().positive().optional(),
  params: FrameSidecarParamsSchema.optional(),
  versions: z.array(CanvasImportVersionSchema).max(10000),
  /** Index into `versions`; the last one when absent. */
  activeVersion: z.number().int().nonnegative().optional(),
}).strict().superRefine((frame, ctx) => {
  if (frame.activeVersion !== undefined && frame.activeVersion >= Math.max(frame.versions.length, 1)) {
    ctx.addIssue({ code: 'custom', path: ['activeVersion'], message: 'The active version must exist on this frame.' });
  }
});
export const CanvasImportSchema = z.object({
  version: z.literal(1),
  title: z.string().min(1).max(500),
  note: z.string().max(4000).optional(),
  /** The document's extent: the Board's first fit, or the scene size when the frames go inside a Compositor. */
  size: z.object({ width: frameSize, height: frameSize }).strict().optional(),
  /**
   * `board` (default): every frame lands on the Board. `composition`: the Board gets one Compositor
   * frame named after the document and every frame goes inside it as a layer, stacked back to front
   * by its draw order (background first, then layer + depth), so the wall is one thing on the Board.
   */
  container: z.enum(['board', 'composition']).optional(),
  /** The scene's colour behind everything (composition container only). */
  background: hexColor.optional(),
  frames: z.array(CanvasImportFrameSchema).max(10000),
}).strict().superRefine((doc, ctx) => {
  const seen = new Set<string>();
  doc.frames.forEach((frame, index) => {
    if (seen.has(frame.key)) ctx.addIssue({ code: 'custom', path: ['frames', index, 'key'], message: `Frame key "${frame.key}" is used twice.` });
    seen.add(frame.key);
  });
  if (doc.container === 'composition') {
    if (doc.frames.length > COMPOSITION_MAX_LAYERS) ctx.addIssue({ code: 'custom', path: ['frames'], message: `A Compositor holds at most ${COMPOSITION_MAX_LAYERS} layers.` });
    doc.frames.forEach((frame, index) => {
      if (frame.kind && frame.kind !== 'media') ctx.addIssue({ code: 'custom', path: ['frames', index, 'kind'], message: 'Only media frames can go inside a Compositor.' });
    });
  }
});
export type CanvasImportVersion = z.infer<typeof CanvasImportVersionSchema>;
export type CanvasImportFrame = z.infer<typeof CanvasImportFrameSchema>;
export type CanvasImport = z.infer<typeof CanvasImportSchema>;

/** Resolved presentation of a frame. URLs belong to the host and are never
 * persisted in place of the portable asset/version identities above. */
export const ArtCanvasFrameSchema = z.object({
  id: graphId, title: z.string(), kind: StudioFrameSchema.shape.kind,
  /** The Compositor frame this one lives in. A frame inside a Compositor is one of its layers, not a Board frame; its x, y, w, h are scene pixels. */
  parentId: graphId.optional(),
  x: coordinate, y: coordinate, w: z.number().positive(), h: z.number().positive(),
  activeVersion: z.number().int().nonnegative(),
  versions: z.array(z.object({
    id: graphId, label: z.string(), gradient: z.string(), image: z.string().optional(),
    mediaType: StudioAssetSchema.shape.mediaType.optional(),
    error: z.string().optional(),
  })),
  params: z.object({
    width: z.number().optional(), height: z.number().optional(), text: z.string().optional(),
    url: z.string().optional(), favorite: z.boolean().optional(),
    values: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
  }).extend(FrameSidecarParamsSchema.shape),
  job: z.object({
    id: graphId, status: z.string(), progress: z.number(), error: z.string().optional(), preview: z.string().optional(),
  }).optional(),
});
export const ArtCanvasLineageSchema = z.object({
  canvasId: graphId,
  nodes: z.array(z.object({
    id: graphId, kind: z.enum(['op', 'output']), label: z.string(), detail: z.string().optional(),
    artboardId: graphId.optional(), idx: z.number().optional(), imageUrl: z.string().nullable().optional(),
  })),
  edges: z.array(z.tuple([graphId, graphId])),
});
export type ArtCanvasFrame = z.infer<typeof ArtCanvasFrameSchema>;
export type ArtCanvasLineage = z.infer<typeof ArtCanvasLineageSchema>;
export type StudioSnapshot = z.infer<typeof StudioSnapshotSchema>;
export type StudioSave = z.infer<typeof StudioSaveSchema>;

/** A selectable image result, independent of its preview resolution. */
export const StudioWorkflowImageSchema = z.object({
  id: graphId, url: z.string(), label: z.string(), jobId: graphId.optional(),
  inputVersions: z.array(StudioFrameInputSchema).optional(),
  settings: StudioParametersSchema.optional(), model: z.string().optional(),
});
export type StudioWorkflowImage = z.infer<typeof StudioWorkflowImageSchema>;


/** File Manager records shared by hosted and desktop presentation adapters. */
export const StudioMediaFileSchema = z.object({
  uuid: z.string(), filename: z.string(), contentType: z.string(), size: z.number(),
  publicUrl: z.string().nullable(), nodeId: z.string().nullable(), folderId: z.string().nullable(),
  status: z.enum(['ACTIVE', 'PROCESSING', 'FAILED', 'DELETED']),
  metadata: z.record(z.string(), z.unknown()).optional(), createdAt: z.string(), updatedAt: z.string().optional(),
})
export type StudioMediaFile = z.infer<typeof StudioMediaFileSchema>
export const StudioMediaFolderSchema = z.object({
  id: z.string(), uuid: z.string(), name: z.string(), fileCount: z.number(), createdAt: z.string(), updatedAt: z.string(),
})
export type StudioMediaFolder = z.infer<typeof StudioMediaFolderSchema>
export const StudioWalletTransactionSchema = z.object({
  id: z.string(),
  // Mirrors WalletTransactionType in packages/db/prisma/schema.prisma.
  type: z.enum(['JOB_DEDUCTION', 'TOP_UP', 'REFUND', 'SUBSCRIPTION_CREDIT', 'TRAINING_HOLD', 'TRAINING_CONFIRMED', 'TRAINING_REFUND', 'ADMIN_DEBIT', 'REFERRAL_CREDIT']),
  amount: z.string(), balanceAfter: z.string(), jobId: z.string().nullable(), description: z.string().nullable(), createdAt: z.string(),
})
export type StudioWalletTransaction = z.infer<typeof StudioWalletTransactionSchema>

/** Desktop hosts persist original web nodes independently of execution graphs. */
export const StudioNodePageSnapshotSchema = z.object({
  revision: z.number().int().nonnegative(), page: StudioWebPageSchema,
}).strict();
export const StudioNodePageSaveSchema = z.object({
  expectedRevision: z.number().int().nonnegative(), page: StudioWebPageSchema,
}).strict();
export const StudioNodeOutputWriteSchema = z.object({
  output: StudioFrameOutputSchema, page: StudioWebPageSchema, graphId,
  dataUrl: z.string().max(34 * 1024 * 1024).regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/),
}).strict();
export type StudioNodePageSnapshot = z.infer<typeof StudioNodePageSnapshotSchema>;
export type StudioNodePageSave = z.infer<typeof StudioNodePageSaveSchema>;
export type StudioNodeOutputWrite = z.infer<typeof StudioNodeOutputWriteSchema>;

/** Queue state shared by the desktop engine and workspace Activity panel. */
export const StudioQueueStateSchema = z.object({
  paused: z.boolean(),
  activeId: z.string().nullable(),
  pendingIds: z.array(z.string()),
  heldIds: z.array(z.string()),
  revision: z.number().int().nonnegative(),
});
export const StudioQueuePauseSchema = z.object({ paused: z.boolean() }).strict();
export const StudioQueueHoldSchema = z.object({ held: z.boolean() }).strict();
export const StudioQueueMoveSchema = z.object({
  id: z.string().min(1), beforeId: z.string().min(1).nullable(), expectedRevision: z.number().int().nonnegative(),
}).strict();
export type StudioQueueState = z.infer<typeof StudioQueueStateSchema>;
export type StudioQueueMove = z.infer<typeof StudioQueueMoveSchema>;
export const StudioActivityStatusSchema = z.enum(['idle', 'queued', 'paused', 'processing', 'success', 'error', 'cancelled']);
export const StudioActivityActionSchema = z.enum(['cancel', 'retry', 'pause', 'resume', 'move-up', 'move-down']);
export const StudioActivityItemSchema = z.object({
  id: z.string(), title: z.string(), description: z.string().optional(), timestamp: z.date().optional(),
  status: StudioActivityStatusSchema, nodeType: z.string().optional(), errorMessage: z.string().optional(),
  progress: z.number().min(0).max(100).optional(),
  actions: z.array(StudioActivityActionSchema).optional(),
  details: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
});
export type StudioActivityStatus = z.infer<typeof StudioActivityStatusSchema>;
export type StudioActivityAction = z.infer<typeof StudioActivityActionSchema>;
export type StudioActivityItem = z.infer<typeof StudioActivityItemSchema>;

export * from "./scene-assets";
export * from "./node-defs";
export * from "./web-operations";
export * from "./execution-inventory";

/** Account-wide immutable bytes; names and trash state use optimistic revisions. */
export const LIBRARY_MAX_FILE_SIZE = 250 * 1024 * 1024;
const libraryFilename = z.string().trim().min(1).max(255).refine(
  value => Array.from(value).every(character => character !== '/' && character !== '\\' && character.charCodeAt(0) >= 32),
  'Use a filename without slashes or control characters.',
);
export const LibraryAssetSchema = StudioAssetSchema.pick({ id: true, name: true, mediaType: true, mimeType: true, size: true, sha256: true }).extend({
  id: z.string().regex(/^[a-f0-9]{64}$/),
  name: libraryFilename,
  size: z.number().int().nonnegative().max(LIBRARY_MAX_FILE_SIZE),
  mimeType: z.string().regex(/^[a-z]+\/[a-z0-9.+-]+$/i).max(160),
}).strict().refine(asset => asset.id === asset.sha256, 'File identity must match its checksum.');
export const LibraryRecordSchema = z.object({
  asset: LibraryAssetSchema, revision: z.number().int().positive(),
  createdAt: z.string().datetime(), updatedAt: z.string().datetime(), deletedAt: z.string().datetime().nullable(),
}).strict();
export const LibraryPageSchema = z.object({ files: z.array(LibraryRecordSchema).max(100), nextCursor: z.string().nullable() }).strict();
export const LibraryChangeSchema = z.object({
  expectedRevision: z.number().int().positive(),
  name: libraryFilename.optional(),
  deleted: z.boolean().optional(),
}).strict();
export const LibrarySyncStatusSchema = z.object({
  state: z.enum(['signed-out', 'syncing', 'synced', 'offline', 'error']),
  pending: z.number().int().nonnegative(), lastSyncedAt: z.string().nullable(), error: z.string().nullable(),
}).strict();
export type LibraryAsset = z.infer<typeof LibraryAssetSchema>;
export type LibraryRecord = z.infer<typeof LibraryRecordSchema>;
export type LibraryPage = z.infer<typeof LibraryPageSchema>;
export type LibraryChange = z.infer<typeof LibraryChangeSchema>;
export type LibrarySyncStatus = z.infer<typeof LibrarySyncStatusSchema>;
export const LibraryIndexStatusSchema = z.object({ state: z.enum(['pending', 'ready', 'error']), error: z.string().nullable() }).strict();
export type LibraryIndexStatus = z.infer<typeof LibraryIndexStatusSchema>;
export const LibraryDeviceFileSchema = z.object({
  asset: LibraryAssetSchema, revision: z.number().int().nonnegative(), createdAt: z.string(),
  localUrl: z.string().nullable(), state: z.enum(['pending', 'synced']),
}).strict();
export const LibraryDeviceListingSchema = z.object({ files: z.array(LibraryDeviceFileSchema), status: LibrarySyncStatusSchema }).strict();
export type LibraryDeviceListing = z.infer<typeof LibraryDeviceListingSchema>;

/**
 * Compositor frame: any media stacked in one scene and arranged on a timeline. Layers are listed
 * bottom to top; positions and sizes are in the composition's own pixels, times in seconds.
 */
export const COMPOSITION_BLENDS = ['normal', 'multiply', 'screen', 'overlay', 'darken', 'lighten'] as const;
const seconds = z.number().finite().min(0).max(3600);
export const COMPOSITION_MODEL_MODES = ['original', 'pointCloud', 'normal', 'wireframe', 'depth', 'clay'] as const;
export const COMPOSITION_UP_DIRECTIONS = ['original', '-x', '+x', '-y', '+y', '-z', '+z'] as const;
const vec3 = z.tuple([coordinate, coordinate, coordinate]);

/** Plain-JSON camera state, the same shape the 3D panel persists on an artboard. */
export const CompositionCameraSchema = z.object({
  position: vec3,
  target: vec3,
  zoom: z.number().finite().positive().max(1e6),
  cameraType: z.enum(['perspective', 'orthographic']),
  fov: z.number().finite().min(1).max(179),
}).strict();
export type CompositionCamera = z.infer<typeof CompositionCameraSchema>;

/** A 3D layer renders live: its own camera, look and turn, in the scene's own time. */
export const CompositionLayer3DSchema = z.object({
  camera: CompositionCameraSchema.optional(),
  /** Turns around the up axis across the clip; 0 holds the start camera. */
  spin: z.number().finite().min(-20).max(20).optional(),
  lightIntensity: z.number().finite().min(0).max(5).optional(),
  /** 'transparent' lets the layers below show through. */
  background: z.union([z.literal('transparent'), hexColor]).optional(),
  grid: z.boolean().optional(),
  mode: z.enum(COMPOSITION_MODEL_MODES).optional(),
  upDirection: z.enum(COMPOSITION_UP_DIRECTIONS).optional(),
  /** Reserved for animation clips; no control writes these yet. */
  clip: z.string().max(200).nullable().optional(),
  clipRate: z.number().finite().min(0).max(4).optional(),
}).strict();
export type CompositionLayer3D = z.infer<typeof CompositionLayer3DSchema>;

export const CompositionLayerSchema = z.object({
  id: graphId,
  name: z.string().min(1).max(200),
  type: z.enum(['image', 'video', 'audio', 'model', 'text']),
  /** The canvas frame the media came from, if any. */
  frameId: graphId.optional(),
  /** Media URL; a 3D layer points at the model file itself. */
  src: z.string().min(1).max(4096).optional(),
  /** 3D layers: how the model is framed, lit and turned. Absent means the viewer's defaults. */
  three: CompositionLayer3DSchema.optional(),
  text: z.string().max(2000).optional(),
  fontFamily: z.string().max(120).optional(),
  fontSize: z.number().finite().min(4).max(2000).optional(),
  fontWeight: z.enum(['normal', 'bold']).optional(),
  color: hexColor.optional(),
  align: z.enum(['left', 'center', 'right']).optional(),
  x: coordinate,
  y: coordinate,
  width: z.number().finite().positive().max(100000),
  height: z.number().finite().positive().max(100000),
  rotation: z.number().finite().min(-360).max(360),
  opacity: z.number().finite().min(0).max(1),
  blend: z.enum(COMPOSITION_BLENDS),
  /** When the layer appears on the timeline. */
  start: seconds,
  duration: z.number().finite().positive().max(3600),
  /** How far into its source a video or audio layer begins playing. */
  offset: seconds,
  volume: z.number().finite().min(0).max(1),
  muted: z.boolean(),
  hidden: z.boolean(),
  locked: z.boolean(),
  /** Layers that share a group id are picked, moved and stacked as one; they sit together in the stack. */
  group: z.string().min(1).max(64).optional(),
}).strict().superRefine((layer, ctx) => {
  if (layer.type === 'text' && layer.text === undefined) ctx.addIssue({ code: 'custom', path: ['text'], message: 'A text layer needs text.' });
  if (layer.type !== 'text' && !layer.src) ctx.addIssue({ code: 'custom', path: ['src'], message: 'A media layer needs a source.' });
  if (layer.three && layer.type !== 'model') ctx.addIssue({ code: 'custom', path: ['three'], message: 'Only a 3D layer has 3D settings.' });
});
/** A scene may be as big as a wall (the exporter scales down to what a canvas can hold). */
export const COMPOSITION_MAX_SIZE = 65536;
/** A wall of cut-outs is hundreds of layers; the stage draws only what is on screen. */
export const COMPOSITION_MAX_LAYERS = 4096;
export const CompositionSchema = z.object({
  width: z.number().int().min(16).max(COMPOSITION_MAX_SIZE),
  height: z.number().int().min(16).max(COMPOSITION_MAX_SIZE),
  fps: z.number().int().min(1).max(120),
  duration: z.number().finite().min(0.1).max(3600),
  background: hexColor,
  layers: z.array(CompositionLayerSchema).max(COMPOSITION_MAX_LAYERS),
}).strict().superRefine((composition, ctx) => {
  const ids = new Set<string>();
  composition.layers.forEach((layer, index) => {
    if (ids.has(layer.id)) ctx.addIssue({ code: 'custom', path: ['layers', index, 'id'], message: 'Layer IDs must be unique.' });
    ids.add(layer.id);
  });
});
export type CompositionLayer = z.infer<typeof CompositionLayerSchema>;
export type Composition = z.infer<typeof CompositionSchema>;
